import * as React from 'react';
import { CartesianGrid, Label, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ChartTooltip from './ChartTooltip';
import { Config } from '../helpers';

const markerFillTypes = {
  filled: 'filled',
  empty: 'empty',
};

const lineStyles = {
  solid: null,
  dashed: "6 4",
  dotted: "2 2",
};

const markerRenderers = {
  circle: ({ cx, cy, stroke, index, isFilled }) => (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
      key={`circle-${index}`}
    />
  ),

  square: ({ cx, cy, stroke, index, isFilled }) => (
    <rect
      x={cx - 4}
      y={cy - 4}
      width={8}
      height={8}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
      key={`square-${index}`}
    />
  ),

  triangle: ({ cx, cy, stroke, index, isFilled }) => (
    <polygon
      key={`triangle-${index}`}
      points={`${cx},${cy - 5} ${cx + 5},${cy + 5} ${cx - 5},${cy + 5}`}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
    />
  ),

  diamond: ({ cx, cy, stroke, index, isFilled }) => (
    <polygon
      key={`diamond-${index}`}
      points={`${cx},${cy - 5} ${cx + 5},${cy} ${cx},${cy + 5} ${cx - 5},${cy}`}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
    />
  ),

  cross: ({ cx, cy, stroke, index }) => (
    <g key={`cross-${index}`} stroke={stroke} strokeWidth={2}>
      <line x1={cx - 5} y1={cy} x2={cx + 5} y2={cy}/>
      <line x1={cx} y1={cy - 5} x2={cx} y2={cy + 5}/>
    </g>
  ),
};

function buildMaps(chartConfig) {
  const colorsMap = {};
  const lineStylesMap = {};
  const markersMap = {};
  const markerFillMap = {};
  chartConfig.colorsMap.forEach(entry => {
    colorsMap[entry.id] = entry.color;
    lineStylesMap[entry.id] = entry.lineStyle;
    markersMap[entry.id] = entry.markerShape;
    markerFillMap[entry.id] = entry.markerFill || markerFillTypes.filled;
  });
  return { colorsMap, lineStylesMap, markersMap, markerFillMap };
}

function resolveByPrefix(map, gid, fallback) {
  let result = fallback;
  Object.entries(map).forEach(([k, v]) => {
    if (gid.startsWith(k.toLowerCase())) result = v;
  });
  return result;
}

function renderLegend(colorsMap, lineStylesMap, markersMap, markerFillMap) {
  return ({ payload }) => (
    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', padding: '10px 0' }}>
      {payload.map((entry, index) => {
        const gid = entry.dataKey.toLowerCase();
        const lineStyleDash = lineStyles[resolveByPrefix(lineStylesMap, gid, null)] ?? null;
        const markerShapeName = resolveByPrefix(markersMap, gid, 'circle');
        const isFilled = resolveByPrefix(markerFillMap, gid, markerFillTypes.filled) === markerFillTypes.filled;

        return (
          <div
            key={`legend-item-${index}`}
            style={{ display: 'flex', alignItems: 'center', marginRight: '20px', marginBottom: '5px' }}
          >
            <svg width="40" height="20" style={{ marginRight: '5px' }}>
              <line x1="0" y1="10" x2="30" y2="10" stroke={entry.color} strokeWidth="2" strokeDasharray={lineStyleDash}/>
              {markerShapeName === 'circle' && (
                <circle cx="15" cy="10" r="4" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'square' && (
                <rect x="11" y="6" width="8" height="8" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'triangle' && (
                <polygon points="15,5 20,15 10,15" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'diamond' && (
                <polygon points="15,5 20,10 15,15 10,10" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'cross' && (
                <g stroke={entry.color} strokeWidth="2">
                  <line x1="10" y1="10" x2="20" y2="10"/>
                  <line x1="15" y1="5" x2="15" y2="15"/>
                </g>
              )}
            </svg>
            <span style={{ fontSize: '14px' }}>{entry.value}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function LineChartWidget({ chartData, chartGroups, chartConfig, lineType = Config.Styles.Chart.DefaultLineStyle }) {
  const [hoveredDot, setHoveredDot] = React.useState(null);
  const { colorsMap, lineStylesMap, markersMap, markerFillMap } = buildMaps(chartConfig);

  return (
    <div>
      <ResponsiveContainer width={Config.Styles.Chart.Width} height={Config.Styles.Chart.Height}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3"/>
          <XAxis
            type="number"
            tickFormatter={value => value}
            dataKey={chartConfig.axes.x.field}
            tick={Config.Styles.Chart.Axis.Tick}
            axisLine={Config.Styles.Chart.Axis.AxisLine}>
            <Label value={chartConfig.axes.x.label} position="bottom" offset={0}/>
          </XAxis>
          <YAxis
            type="number"
            tickFormatter={value => value}
            tick={Config.Styles.Chart.Axis.Tick}
            axisLine={Config.Styles.Chart.Axis.AxisLine}>
            <Label value={chartConfig.axes.y.label} angle={-90} position="inside" dx={-35}/>
          </YAxis>
          <Tooltip cursor={false} isAnimationActive={false} content={<ChartTooltip chartConfig={chartConfig} hoveredDot={hoveredDot}/>}/>
          <Legend content={renderLegend(colorsMap, lineStylesMap, markersMap, markerFillMap)}/>

          {chartGroups.map((g) => {
            const gid = g.id.toLowerCase();
            const color = resolveByPrefix(colorsMap, gid, '#000');
            const lineStyle = lineStyles[resolveByPrefix(lineStylesMap, gid, null)] ?? null;
            const markerShape = markerRenderers[resolveByPrefix(markersMap, gid, 'circle')] ?? markerRenderers.circle;
            const isFilled = resolveByPrefix(markerFillMap, gid, markerFillTypes.filled) === markerFillTypes.filled;

            return (
              <Line
                key={g.id}
                dataKey={g.id}
                name={g.label}
                connectNulls={true}
                type={lineType}
                strokeWidth={2}
                stroke={color}
                strokeDasharray={lineStyle}
                dot={({ value, payload: dotPayload, ...p }) => {
                  if (value === null) return null;
                  return (
                    <g
                      key={`dot-${g.id}-${p.index}`}
                      onMouseEnter={() => setHoveredDot({ dataKey: g.id, entry: dotPayload[`${g.id}__entry`], stroke: color })}
                      onMouseLeave={() => setHoveredDot(null)}
                    >
                      <circle cx={p.cx} cy={p.cy} r={10} fill="transparent"/>
                      {markerShape({...p, isFilled})}
                    </g>
                  );
                }}
                activeDot={null}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
